import { Tabs } from "radix-ui";
import { Dialog } from "radix-ui";
import Button from "@/components/bricks/button/Button";
import { Formik, Form, Field, FormikHelpers } from "formik";
import * as Yup from "yup";
import {
  QueryFilter,
  FilterPreset,
  FilterKeys,
} from "../../../interfaces/filters";
import { post } from "../../../api/util";
import { Dispatch, SetStateAction, useContext } from "react";
import { DEFAULT_ERROR } from "../../../constants/errors";
import {
  FilterPresetsContext,
  FilterPresetsEventContext,
} from "../../../context/FiltersContext";
import useLocalStorageState from "use-local-storage-state";
import IsRequired from "../../formcontrols/common/IsRequired";
import InputErrorMessage from "../../InputErrorMessage";
import { UserQueryContext } from "../../../context/UserContext";

const FiltersSaveTabGemNytFilter = (props: {
  contextType: FilterPreset["type"];
  setOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  // props
  const { contextType, setOpen } = props;

  // context
  const filterPresets = useContext(FilterPresetsContext);
  const filterPresetsEvent = useContext(FilterPresetsEventContext);
  const { user } = useContext(UserQueryContext);

  // state and storage
  const [order, setOrder] = useLocalStorageState<string[]>(
    `${contextType}FilterOrder`,
  );

  // case gymnastics
  // lowercase context type to match the correct key in localStorage
  const contextCased =
    contextType.charAt(0).toLowerCase() + contextType.slice(1);

  const [filters] = useLocalStorageState<QueryFilter<FilterKeys>[]>(
    `${contextCased}Filters`,
  );

  // functions
  async function saveFilter(
    values: FilterPresetValues,
    actions?: FormikHelpers<FilterPresetValues>,
  ) {
    try {
      const updatedFilter: Omit<FilterPreset, "filter"> = {
        ...values,
        erPersonlig: values.erPersonlig === "savetoPersonlig",
      };

      const result = await post<SoegefilterDTO>({
        query: `brugere/${user?.id}/soegefiltre`,
        body: {
          ...updatedFilter,
          filter: JSON.stringify(filters ?? []),
        },
      });

      const filterForLocal = [
        ...filterPresets,
        { ...result, filter: JSON.parse(result.filter) },
      ];

      if (order) {
        setOrder([...order, result.id]);
      }

      filterPresetsEvent({
        event: "set",
        filters: filterForLocal,
      });
    } catch (error: any) {
      actions?.setStatus(error?.data?.title ?? DEFAULT_ERROR.message);
      throw error;
    }
    setOpen(false);
  }

  return (
    <Tabs.Content value="gemNytFilter">
      <Formik
        initialValues={
          {
            navn: "",
            erPersonlig: "savetoPersonlig",
            filter: "",
            type: contextType,
          } as FilterPresetValues
        }
        validationSchema={Yup.object({
          navn: Yup.string()
            .test("", "Filternavn er brugt", (input) => {
              return (
                filterPresets.filter((filter) => filter.navn === input)
                  .length === 0
              );
            })
            .required("Filternavn er påkrævet"),
          erPersonlig: Yup.string(),
        })}
        onSubmit={(values, actions) => saveFilter(values, actions)}
      >
        {({
          values,
          handleSubmit,
          handleChange,
          handleBlur,
          errors,
          status,
          setStatus,
        }) => {
          if (errors.navn && status !== undefined) {
            setStatus(undefined);
          }

          return (
            <Form onSubmit={handleSubmit}>
              <div className="c-item-list p-3">
                <div className="flex-100 mt-2">
                  <label className="form-label" htmlFor="saveName">
                    Gem som
                    <IsRequired />
                  </label>
                  <Field
                    className="form-control"
                    id="saveName"
                    type="text"
                    placeholder="Filternavn"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.navn}
                    name="navn"
                  />
                </div>
                <div className="flex-100 d-block">
                  <h5 className="form-label mb-2 d-block">Filtertype</h5>
                  <div className="form-check form-check-inline">
                    <Field
                      className="form-check-input"
                      type="radio"
                      checked={values.erPersonlig === "savetoPersonlig"}
                      name="erPersonlig"
                      id="savetoPersonlig"
                      value="savetoPersonlig"
                    />
                    <label
                      className="form-check-label"
                      htmlFor="savetoPersonlig"
                    >
                      Personlig
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <Field
                      className="form-check-input"
                      type="radio"
                      name="erPersonlig"
                      id="savetoStandard"
                      checked={values.erPersonlig === "savetoStandard"}
                      value="savetoStandard"
                    />
                    <label
                      className="form-check-label"
                      htmlFor="savetoStandard"
                    >
                      Standardfilter
                    </label>
                  </div>
                </div>
              </div>
              {errors.navn && (
                <InputErrorMessage
                  className="px-3"
                  id="feedback"
                  value={`*${errors.navn}`}
                />
              )}
              {status && !errors.navn && (
                <InputErrorMessage
                  className="px-3"
                  id="feedback"
                  value={`${status}`}
                />
              )}

              <div className="flex-100 p-3 d-flex justify-content-between gap-2">
                <Dialog.Close asChild>
                  <Button color="secondary">Annuller</Button>
                </Dialog.Close>
                <Button type="submit" disabled={!!errors.navn} color="primary">
                  Gem
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Tabs.Content>
  );
};

export default FiltersSaveTabGemNytFilter;
